import { useLocation, useNavigate } from "react-router";
import { SkyButton } from "@skypointcloud/skypoint-ui";
import { getInstanceIdFromUrl } from "../utils/instanceId";
import { leaveWhenAllowed } from "./shared/leaveGuard";
import { hubBackLink } from "../views/dashboard/sidebar/settingsHub";
import { useShellNavSections } from "../views/dashboard/sidebar/useShellNavSections";

/**
 * "← Back to Settings" (2026-10-08 shell spec, D5; skyAdvisor's GlobalSetupBackLink): on every
 * settings page, back to the tiled Settings page of the product the user is in, so a settings page
 * opened from a tile has a way back that is not the browser's. Rendered once, by the layout, above
 * the page, so no settings page changes. Which pages are settings pages, and which tiled page they
 * belong to, is the shell's navigation data (hubBackLink): the same entries the tiles show.
 *
 * The template pages get "← Back to Templates" the same way: the Templates item (Templates & Setup)
 * is a hub like Settings, and the link is named for the hub the page belongs to.
 *
 * Skypoint UI's transparent small button, as skyAdvisor's, in the page frame's gutters
 * (`.sky-page`: 28px, 16px below 768px). It tucks into the page's own top padding (P2-5): a short
 * top, and on a desktop 8px of the frame's 24px taken back, so the title or crumb sits 16px under
 * it at every width. The instance stays in the query, as every app link keeps it.
 */
export function SettingsBackLink() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { sections } = useShellNavSections();
  const link = hubBackLink(pathname, sections);
  if (!link) return null;
  const target = link.route;
  const instanceId = getInstanceIdFromUrl();
  return (
    <div className="px-4 pt-3 sm:-mb-2 sm:px-7 print:hidden">
      <SkyButton
        appearance="transparent"
        size="small"
        // A page with unsaved edits asks first (leaveGuard), as its own ways out always did.
        onClick={() =>
          leaveWhenAllowed(() =>
            navigate(instanceId ? `${target}?instanceid=${instanceId}` : target),
          )
        }
      >
        &#8592; Back to {link.label}
      </SkyButton>
    </div>
  );
}

export default SettingsBackLink;
